import React, {createContext, useContext, useEffect, useMemo} from 'react';

import {envIsLocal, teleportAdminCheckEnabled} from '../../../config';
import {useViewer} from '../../../state/viewer/hooks';
import {
  setViewerUsingAdminPrivileges,
  useAdminModeActive,
  viewerIsWbAdmin,
  viewingAs,
} from '../../../util/admin';
import {useIsSignupRoute} from './profileMenuUtils';
import {useTeleportAccessRequests} from './useTeleportAccessRequests';

type ProfileMenuContextValue = {
  approvedTeleportAccessRequests: Record<string, string>;
  isAdminModeActive: boolean;
  isImpersonating: boolean;
  isFetchingApprovedTeleportAccessRequests: boolean;
  isFullyConnected: boolean;
  isSignup: boolean;
  shouldShowAdminTools: boolean;
  teleportAdminCheckIsEnabled: boolean;
};

const ProfileMenuContext = createContext<ProfileMenuContextValue | null>(null);

export function ProfileMenuContextProvider({
  isFullyConnected,
  children,
}: {
  isFullyConnected?: boolean;
  children: React.ReactNode;
}) {
  const viewer = useViewer();
  const wasWbAdminWhenCheckingForTeleportAccessRequests =
    viewerIsWbAdmin(viewer);
  const isAdminMode = useAdminModeActive();
  const isImpersonating = !envIsLocal && viewingAs() !== '';

  const teleportAdminCheckIsEnabled = teleportAdminCheckEnabled();

  const {
    approvedTeleportAccessRequests,
    isFetchingApprovedTeleportAccessRequests,
  } = useTeleportAccessRequests(
    wasWbAdminWhenCheckingForTeleportAccessRequests
  );

  const hasApprovedTeleportAccessRequest =
    Object.keys(approvedTeleportAccessRequests).length > 0;

  const shouldShowAdminTools =
    !teleportAdminCheckIsEnabled || hasApprovedTeleportAccessRequest;

  // We need to check !isFetchingApprovedTeleportAccessRequests to avoid
  // prematurely setting adminEnabledWithoutApprovedTeleportAccessRequest
  // to true. Removing this check would unset the admin cookie before the
  // call to useTeleportAccessRequests() returns
  const adminEnabledWithoutApprovedTeleportAccessRequest =
    !isFetchingApprovedTeleportAccessRequests &&
    teleportAdminCheckIsEnabled &&
    isAdminMode &&
    !hasApprovedTeleportAccessRequest;

  // Note: During impersonation, viewer is null on the first few calls to
  // useViewer(). Without the isWbAdmin guard,
  // useTeleportAccessRequests() will return false for the null viewer and
  // we would prematurely unset the admin cookie, breaking impersonation.
  // Also, when we are impersonating wandb admins,
  // isWbAdmin and adminEnabledWithoutApprovedTeleportAccessRequest
  // could both be true since the viewer is derived from the impersonated
  // user, so we don't unset the admin cookie on impersonation
  // to avoid breaking impersonation on other wandb admins.
  const shouldUnsetAdminPrivileges =
    wasWbAdminWhenCheckingForTeleportAccessRequests &&
    adminEnabledWithoutApprovedTeleportAccessRequest &&
    !isImpersonating;

  useEffect(() => {
    if (shouldUnsetAdminPrivileges) {
      setViewerUsingAdminPrivileges(false);
    }
  }, [shouldUnsetAdminPrivileges]);

  const isAdminModeActive = isAdminMode && !shouldUnsetAdminPrivileges;

  const isSignup = useIsSignupRoute();

  const value = useMemo(
    () => ({
      approvedTeleportAccessRequests,
      isAdminModeActive,
      isImpersonating,
      isFetchingApprovedTeleportAccessRequests,
      isFullyConnected: !!isFullyConnected,
      isSignup,
      shouldShowAdminTools,
      teleportAdminCheckIsEnabled,
    }),
    [
      approvedTeleportAccessRequests,
      isAdminModeActive,
      isFetchingApprovedTeleportAccessRequests,
      isFullyConnected,
      isImpersonating,
      isSignup,
      shouldShowAdminTools,
      teleportAdminCheckIsEnabled,
    ]
  );

  return (
    <ProfileMenuContext.Provider value={value}>
      {children}
    </ProfileMenuContext.Provider>
  );
}

export function useProfileMenuContext(): ProfileMenuContextValue {
  const context = useContext(ProfileMenuContext);
  if (context === null) {
    throw new Error(
      'useProfileMenuContext must be used within a ProfileMenuContextProvider'
    );
  }
  return context;
}
